import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FirebaseStorageService } from '../services/firebase-storage.service';

@Component({
  selector: 'app-agenda-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="agenda-upload-container">
      <div class="agenda-actions">
        <button type="button" class="upload-btn" (click)="triggerFilePicker()" [disabled]="isUploading">
          <span *ngIf="!isUploading">Upload Agenda PDF</span>
          <span *ngIf="isUploading">Uploading...</span>
        </button>

        <button *ngIf="agendaUrl" type="button" class="preview-btn" (click)="previewAgenda()">Preview</button>
        <button *ngIf="agendaUrl" type="button" class="delete-btn" (click)="removeAgenda()">Delete</button>
      </div>

      <input #fileInput type="file" accept="application/pdf" style="display:none" (change)="onFileSelected($event)" />

      <div *ngIf="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>
  `,
  styles: [`
    .agenda-upload-container { display: flex; flex-direction: column; gap: 8px; }
    .agenda-actions { display: flex; gap: 8px; align-items: center; }
    .upload-btn, .preview-btn, .delete-btn {
      padding: 10px 14px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;
    }
    /* coral-deep, not coral: this fill carries cream text (2.89:1 otherwise) */
    .upload-btn { background: #1F6F66; color: #FFF6EE; }
    .upload-btn[disabled] { opacity: 0.6; cursor: not-allowed; }
    .preview-btn { background: transparent; color: #12332F; border: 1.5px solid rgba(18, 51, 47, 0.14); }
    .delete-btn { background: rgba(166, 38, 57, .10); color: #A62639; border: 1px solid rgba(166, 38, 57, .45); }
    .error-message { color: #A62639; font-size: 12px; }
  `]
})
export class AgendaUploadComponent {
  @Input() eventId: string | null = null;
  @Input() currentUrl: string | null = null;
  @Output() agendaUploaded = new EventEmitter<string>();
  @Output() agendaRemoved = new EventEmitter<void>();

  agendaUrl: string | null = null;
  isUploading = false;
  errorMessage = '';

  constructor(private storageService: FirebaseStorageService) {}

  ngOnInit(): void {
    this.agendaUrl = this.currentUrl ?? null;
  }

  ngOnChanges(): void {
    if (this.currentUrl !== this.agendaUrl) {
      this.agendaUrl = this.currentUrl ?? null;
    }
  }

  triggerFilePicker(): void {
    const el = document.createElement('input');
    el.type = 'file';
    el.accept = 'application/pdf';
    el.style.display = 'none';
    el.addEventListener('change', (e: Event) => this.onFileSelected(e));
    document.body.appendChild(el);
    el.click();
    setTimeout(() => document.body.removeChild(el), 0);
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      this.errorMessage = 'Please select a PDF file';
      return;
    }
    // 5MB limit (adjust as needed)
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.errorMessage = 'File size must be less than 5MB';
      return;
    }

    if (!this.eventId) {
      this.errorMessage = 'Missing event ID. Cannot upload.';
      return;
    }

    this.errorMessage = '';
    this.isUploading = true;
    try {
      const url = await this.storageService.uploadAgenda(file, this.eventId);
      if (url) {
        this.agendaUrl = url;
        this.agendaUploaded.emit(url);
      } else {
        this.errorMessage = 'Failed to upload agenda PDF. Please try again.';
      }
    } catch (e) {
      console.error('Agenda upload failed', e);
      this.errorMessage = 'Failed to upload agenda PDF. Please try again.';
    } finally {
      this.isUploading = false;
    }
  }

  previewAgenda(): void {
    if (this.agendaUrl) window.open(this.agendaUrl, '_blank');
  }

  removeAgenda(): void {
    this.agendaUrl = null;
    this.agendaRemoved.emit();
  }
}

